import { Badge, Text } from '@thefarmersfront/kitchen';

import type {
  ColDef,
  GridOptions,
  ICellRendererParams,
  RowHeightParams,
  ValueFormatterParams,
} from 'ag-grid-community';

import { MultiRowCell, TextButtonCell } from '~/components/common';

import { formatNumber } from '~/utils/common';
import { formatDate } from '~/utils/date';

import { DATE_FORMAT } from '~/constants';
import { CLAIM_RETURN_INBOUND_STATUS_BADGE_COLOR, CLAIM_RETURN_INSPECTION_STATUS_BADGE_COLOR } from '~/constants/claim';

import type { TypeExchangeDetailData } from '~/types/claim.types';

type TypeReturnGoodsInspection = NonNullable<TypeExchangeDetailData['returnOrder']['returnGoodsInspections']>[number];

/**
 * [교환 신청/조회] 신청 상세 모달 > '검수 처리 상세' 테이블 컬럼 정의
 */
const getClaimExchangeDetailInspectionColumnDefs = ({
  onClickViewInspectionImage,
}: {
  onClickViewInspectionImage: (imageList: string[]) => void;
}) => {
  const gridOptions: GridOptions = {
    getRowHeight: ({ data }: RowHeightParams<TypeReturnGoodsInspection>) => {
      return data?.inspectionDetails?.length ? data.inspectionDetails.length * 40 : 40;
    },
    columnTypes: {
      multiRow: { cellStyle: { padding: 0, border: 0 } },
    },
  };

  const columnDefs: ColDef<TypeReturnGoodsInspection>[] = [
    {
      headerName: 'No',
      valueGetter: 'node.rowIndex + 1',
      width: 72,
    },
    {
      field: 'goodsCode',
      headerName: '상품코드',
      width: 130,
      minWidth: 130,
      flex: 1,
    },
    {
      field: 'goodsName',
      headerName: '상품명',
      width: 260,
    },
    {
      field: 'returnQuantity',
      headerName: '교환신청수량',
      width: 100,
      valueFormatter: ({
        value,
      }: ValueFormatterParams<TypeReturnGoodsInspection, TypeReturnGoodsInspection['returnQuantity']>) =>
        value == null || Number.isNaN(value) ? '-' : formatNumber(value),
    },
    {
      field: 'returnReason.name',
      headerName: '교환사유',
      width: 100,
    },
    {
      field: 'inspectionDetails',
      headerName: '검수수량',
      cellDataType: false,
      width: 80,
      cellRenderer: MultiRowCell,
      cellRendererParams: {
        field: 'inspectionQuantity',
        itemRenderer: Text,
        itemRendererParams: ({
          value,
        }: ICellRendererParams<
          TypeReturnGoodsInspection,
          TypeReturnGoodsInspection['inspectionDetails'][number]['inspectionQuantity']
        >) => ({
          size: 'large',
          children: value ? formatNumber(value) : '-',
        }),
      },
      type: 'multiRow',
    },
    {
      field: 'inspectionDetails',
      headerName: '입하상태',
      cellDataType: false,
      width: 100,
      cellRenderer: MultiRowCell,
      cellRendererParams: {
        field: 'inboundStatus',
        itemRenderer: Badge,
        itemRendererParams: ({
          value,
        }: ICellRendererParams<
          TypeReturnGoodsInspection,
          TypeReturnGoodsInspection['inspectionDetails'][number]['inboundStatus']
        >) => ({
          variant: 'light',
          status: true,
          color: CLAIM_RETURN_INBOUND_STATUS_BADGE_COLOR[value as keyof typeof CLAIM_RETURN_INBOUND_STATUS_BADGE_COLOR],
          children: value,
        }),
      },
      type: 'multiRow',
    },
    {
      field: 'inspectionDetails',
      headerName: '검수상태',
      cellDataType: false,
      width: 100,
      cellRenderer: MultiRowCell,
      cellRendererParams: {
        field: 'inspectionStatus',
        itemRenderer: Badge,
        itemRendererParams: ({
          value,
        }: ICellRendererParams<
          TypeReturnGoodsInspection,
          TypeReturnGoodsInspection['inspectionDetails'][number]['inspectionStatus']
        >) => ({
          variant: 'light',
          status: true,
          color:
            CLAIM_RETURN_INSPECTION_STATUS_BADGE_COLOR[
              value as keyof typeof CLAIM_RETURN_INSPECTION_STATUS_BADGE_COLOR
            ],
          children: value,
        }),
      },
      type: 'multiRow',
    },
    {
      field: 'inspectionDetails',
      headerName: '검수이미지',
      cellDataType: false,
      width: 92,
      cellRenderer: MultiRowCell,
      cellRendererParams: {
        field: 'inspectionImageUrls',
        itemRenderer: TextButtonCell,
        itemRendererParams: ({
          value,
        }: ICellRendererParams<
          TypeReturnGoodsInspection,
          TypeReturnGoodsInspection['inspectionDetails'][number]['inspectionImageUrls']
        >) => ({
          variant: 'normal',
          children: '보기',
          onClick: () => {
            if (!value) return;
            onClickViewInspectionImage(value);
          },
        }),
      },
      type: 'multiRow',
    },
    {
      field: 'inspectionDetails',
      headerName: '검수일시',
      cellDataType: false,
      width: 167,
      cellRenderer: MultiRowCell,
      cellRendererParams: {
        field: 'inspectionAt',
        itemRenderer: Text,
        itemRendererParams: ({
          value,
        }: ICellRendererParams<
          TypeReturnGoodsInspection,
          TypeReturnGoodsInspection['inspectionDetails'][number]['inspectionAt']
        >) => ({
          size: 'large',
          children: formatDate(value, DATE_FORMAT.DATE_TIME) ?? '-',
        }),
      },
      type: 'multiRow',
    },
  ];

  return { gridOptions, columnDefs };
};

export default getClaimExchangeDetailInspectionColumnDefs;
